import { useEffect, useCallback, useState, useContext } from 'react';
import { FaFileDownload } from 'react-icons/fa';
import { FcOk, FcInfo, FcCancel } from 'react-icons/fc';
import { FiSquare } from 'react-icons/fi';
// import { TiCancel } from 'react-icons/ti';
import ReactTooltip from 'react-tooltip';

import PropTypes from 'prop-types';
import styled, { keyframes } from 'styled-components';

import { AuthContext } from '../../../../actions/auth-context';
import {
  getSW3ThumbnailUrl,
  addPhotosToOrder,
  deletePhotosOfOrder,
  downloadEditedPhotosFromOrder,
  downloadOrderPhoto,
  updateEditedPhotosClassification,
} from '../../../../actions/order.service';
import {
  expectedPhotosByProducts,
  PHOTO_SIZES,
  PHOTO_CLASSIFICATIONS,
} from '../../../../commons/order-checklist';
import DeleteModal from '../../../../components/delete-modal';
import S3Image from '../../../../components/s3-image';
import { InputFile } from './action-buttons/AddPhotosButton';
import ViewImageModal from './ViewImageModal';

// Upload de editadas (FEAT-018): editor durante 'Em edição'; admin/diretor a
// partir do financeiro aprovado (supervisão) — mesma regra do antigo botão
// genérico "add fotos editadas". Exportada para a aba de galerias decidir se a
// aba "Fotos Editadas" aparece mesmo sem fotos.
export const canUploadEditedPhotos = (
  hasRoles,
  hasPermissions,
  orderStatus
) => {
  if (
    hasRoles(['administrator', 'master']) &&
    orderStatus !== 'Em inclusão' &&
    orderStatus !== 'Incluído' &&
    orderStatus !== 'Análise financeira' &&
    orderStatus !== 'Financeiro aprovado' &&
    orderStatus !== 'Financeiro negado'
  )
    return true;

  if (
    orderStatus === 'Em edição' &&
    hasPermissions(['edit_photos', 'edit_order']) &&
    !hasRoles(['printer', 'fitter', 'packer', 'plotter', 'seller'])
  )
    return true;

  return false;
};

// Produção vê SÓ O QUE IMPRIME (decisão do dono 05/07): impressor = avulsas/
// sem classificação não-plotter (o fotolivro ele imprime pelas LÂMINAS);
// plotter = somente os tamanhos de produto plotter. Supervisão (admin/diretor)
// e edição/inspeção continuam vendo a galeria completa. Exportada para a aba
// de galerias usar a mesma contagem.
export const visibleEditedPhotos = (hasRoles, hasPermissions, order) => {
  const edited = (order.photos || []).filter((p) => p.photo_type === 'Editada');

  const fullView =
    hasRoles(['master', 'administrator']) ||
    hasPermissions([
      'edit_order',
      'edit_photos',
      'edit_inspection',
      'seller_inspection',
    ]);

  if (fullView) return edited;

  const plotterSizes = new Set(
    (order.products || [])
      .filter((p) => p.type === 'Impressão plotter')
      .map((p) => p.photo_size)
      .filter(Boolean)
  );

  const printsLab = hasPermissions(['print_photos']);
  const printsPlotter = hasPermissions(['plotter_photos']);

  return edited.filter((photo) => {
    const isPlotterPhoto =
      photo.photo_size && plotterSizes.has(photo.photo_size);

    if (isPlotterPhoto) return printsPlotter;

    return printsLab && photo.photo_classification !== 'Fotolivro';
  });
};

const EditedPhotos = ({ order, setOrder, getOrder }) => {
  const { setLoading, hasPermissions, hasRoles } = useContext(AuthContext);

  const [thumbnails, setThumbnails] = useState([]);

  const [firstPhotoToView, setFirstPhotoToView] = useState({});

  // Seleção unificada (mesma usada para baixar e para remover selecionadas).
  const [selectedIds, setSelectedIds] = useState([]);

  // Estado local para loader das fotos
  const [isLoadingPhotos, setIsLoadingPhotos] = useState(false);

  // Tamanho/classificação a aplicar nas selecionadas (FEAT-018).
  const [assignSize, setAssignSize] = useState('');

  const [assignClassification, setAssignClassification] = useState('');

  // Fotos editadas = SOMENTE photo_type 'Editada'. As capturadas (brutas) e as
  // lâminas têm suas próprias seções (CapturedPhotos e SpreadPhotos) e não
  // entram nesta galeria. Legado já é 'Editada' (server_default da migration).
  // Impressor/plotter veem SÓ o que imprimem (visibleEditedPhotos, 05/07).
  const allEditedPhotos = (order.photos || []).filter(
    (item) => item.photo_type === 'Editada'
  );

  const editedPhotos = visibleEditedPhotos(hasRoles, hasPermissions, order);

  // Capa avaliada sobre TODAS as editadas — a capa pode ser uma foto fora do
  // recorte do impressor/plotter e o alerta não deve disparar à toa.
  const validPhotos =
    allEditedPhotos.filter((item) => item.is_cover_photo === true).length > 0;

  // FEAT-018: validação DURA — o select só oferece os tamanhos dos PRODUTOS do
  // pedido (mesma regra das capturadas). Sem restrição com 'Pacote' ou nenhum
  // produto com tamanho parametrizado.
  const hasPackage = (order.products || []).some((p) => p.type === 'Pacote');

  const productSizes = [
    ...new Set((order.products || []).map((p) => p.photo_size).filter(Boolean)),
  ];

  const availableSizes =
    hasPackage || productSizes.length === 0
      ? PHOTO_SIZES
      : PHOTO_SIZES.filter((size) => productSizes.includes(size));

  // FEAT-015/018: quantidades ESPERADAS por classificação/tamanho (fonte única:
  // commons/order-checklist — inclui os ADICIONAIS de álbum).
  const {
    album: expectedAlbumBySize,
    avulsa: expectedAvulsaBySize,
    albumTotal: expectedAlbumPhotos,
  } = expectedPhotosByProducts(order);

  const expectedAvulsaPhotos = Object.values(expectedAvulsaBySize).reduce(
    (sum, value) => sum + value,
    0
  );

  // Esperado de um subgrupo (tamanho) conforme a seção de classificação.
  const expectedForGroup = (sectionKey, size) => {
    if (sectionKey === 'Fotolivro') return expectedAlbumBySize[size] || null;
    if (sectionKey === 'Foto avulsa') return expectedAvulsaBySize[size] || null;
    return null;
  };

  // FEAT-018 UX: botões de upload por (tamanho × classificação) derivados dos
  // produtos do pedido — a foto editada já nasce com tamanho e classificação.
  const uploadButtons = [];

  (order.products || []).forEach((p) => {
    if (!p.photo_size) return;

    const classification = p.type === 'Fotolivro' ? 'Fotolivro' : 'Foto avulsa';

    const exists = uploadButtons.some(
      (b) => b.size === p.photo_size && b.classification === classification
    );

    if (!exists) uploadButtons.push({ size: p.photo_size, classification });
  });

  uploadButtons.sort(
    (a, b) =>
      (a.classification === 'Fotolivro' ? 0 : 1) -
        (b.classification === 'Fotolivro' ? 0 : 1) ||
      PHOTO_SIZES.indexOf(a.size) - PHOTO_SIZES.indexOf(b.size)
  );

  const canUpload = useCallback(
    () => canUploadEditedPhotos(hasRoles, hasPermissions, order.order_status),
    [order]
  );

  const onChangePhoto = useCallback(
    async (e, photoSize, photoClassification) => {
      e.preventDefault();

      setLoading(true);

      let files;

      if (e.dataTransfer) {
        files = e.dataTransfer.files;
      } else if (e.target) {
        files = e.target.files;
      }

      const formData = new FormData();

      for (let i = 0; i < files.length; i++) {
        formData.append('photos', files[i]);
      }

      // FEAT-018 UX: upload pelo botão "add fotos/álbum {tamanho}" já carimba
      // tamanho E classificação (o backend valida contra os produtos).
      if (photoSize) formData.append('photo_size', photoSize);
      if (photoClassification)
        formData.append('photo_classification', photoClassification);

      const response = await addPhotosToOrder(order.id, formData);

      if (!response.error) {
        response.delivery_date = new Date(response.delivery_date);

        if (response.payment_first_date) {
          response.payment_first_date = new Date(
            response.payment_first_date.replace(/-/g, ',')
          );
        }

        setOrder(response);
      }

      setLoading(false);
    },
    [order]
  );

  // Baixar as fotos editadas: impressor só DURANTE a impressão ('Em impressão');
  // plotter só DURANTE o plotter ('Em plotter') — FEAT-015 fase 3; admin/diretor
  // sem restrição de status (FEAT-010).
  const canDownload =
    hasRoles(['master', 'administrator']) ||
    (hasPermissions(['print_photos']) &&
      order.order_status === 'Em impressão') ||
    (hasPermissions(['plotter_photos']) && order.order_status === 'Em plotter');

  // Rótulo do botão diz o que VEM no ZIP (recorte por etapa, 05/07): impressor
  // recebe só as avulsas não-plotter; plotter só os tamanhos de plotter;
  // admin/diretor recebe tudo.
  const downloadAllLabel = () => {
    if (hasRoles(['master', 'administrator'])) return 'baixar todas';
    if (order.order_status === 'Em impressão') return 'baixar avulsas';
    if (order.order_status === 'Em plotter') return 'baixar fotos plotter';
    return 'baixar todas';
  };

  // Excluir fotos: SOMENTE o EDITOR (dono) enquanto edita ('Em edição' ou 'Em
  // edição de lâminas') e admin/diretor em qualquer status. Inspetor, vendedor e
  // impressor NÃO removem (impressor só baixa para imprimir).
  const canRemove = useCallback(() => {
    if (hasRoles(['master', 'administrator'])) return true;

    if (
      (order.order_status === 'Em edição' ||
        order.order_status === 'Em edição de lâminas') &&
      hasPermissions(['edit_photos', 'edit_order'])
    )
      return true;

    return false;
  }, [order]);

  // Classificar (Fotolivro/Foto avulsa) as editadas: o EDITOR durante a edição
  // ('Em edição'/'Em edição de lâminas') e admin/diretor. FEAT-013.
  const canClassify = useCallback(() => {
    if (hasRoles(['master', 'administrator'])) return true;

    if (
      (order.order_status === 'Em edição' ||
        order.order_status === 'Em edição de lâminas') &&
      hasPermissions(['edit_photos', 'edit_order'])
    )
      return true;

    return false;
  }, [order]);

  const downloadAll = useCallback(async () => {
    setLoading(true);

    await downloadEditedPhotosFromOrder(order.id, order.person?.full_name);

    setLoading(false);
  }, [order]);

  // Aplica tamanho e/ou classificação às fotos editadas selecionadas (FEAT-018).
  const applyAttributes = useCallback(async () => {
    if (selectedIds.length === 0) return;
    if (!assignSize && !assignClassification) return;

    setLoading(true);

    const payload = { ids: selectedIds };
    if (assignSize) payload.photo_size = assignSize;
    if (assignClassification)
      payload.photo_classification = assignClassification;

    const response = await updateEditedPhotosClassification(order.id, payload);

    if (!response.error) {
      response.delivery_date = new Date(response.delivery_date);

      if (response.payment_first_date) {
        response.payment_first_date = new Date(
          response.payment_first_date.replace(/-/g, ',')
        );
      }

      setOrder(response);

      setSelectedIds([]);
    }

    setLoading(false);
  }, [selectedIds, order, assignSize, assignClassification]);

  // Baixar as selecionadas: uma a uma (cada foto vira um arquivo), sequencial
  // para não disparar várias requisições simultâneas ao S3/local.
  const downloadSelected = useCallback(async () => {
    setLoading(true);

    await selectedIds.reduce(async (prev, id) => {
      await prev;
      const photo = editedPhotos.find((item) => item.id === id);
      return downloadOrderPhoto(order.id, id, photo?.file_name);
    }, Promise.resolve());

    setLoading(false);
  }, [selectedIds, editedPhotos, order]);

  // Baixar UMA foto original pelo ícone da barrinha do card (20/07) — mesma
  // mecânica das capturadas.
  const downloadOne = useCallback(
    async (photo_id) => {
      const photo = editedPhotos.find((item) => item.id === photo_id);

      setLoading(true);

      await downloadOrderPhoto(order.id, photo_id, photo?.file_name);

      setLoading(false);
    },
    [order, editedPhotos]
  );

  const getLocalBlobThumbnail = useCallback(
    async (photo) => {
      // FEAT-011: presigned URL do S3 (antes baixava o blob pela API).
      const result = await getSW3ThumbnailUrl(order.id, photo.id);

      return {
        id: photo.id,
        blob: result,
        photo_status: photo.photo_status,
        is_cover_photo: photo.is_cover_photo,
        note: photo.note,
        photo_size: photo.photo_size,
        photo_classification: photo.photo_classification,
        created_at: photo.created_at,
      };
    },
    [order]
  );

  const prepareThumbnails = useCallback(async () => {
    if (editedPhotos.length > 0) {
      setIsLoadingPhotos(true);

      const list = [];

      for (let i = 0; i < editedPhotos.length; i++) {
        const photo = editedPhotos[i];

        list.push(getLocalBlobThumbnail(photo));
      }

      const result = await Promise.all(list);

      // Ordena pela data de UPLOAD (created_at), não pelo id (UUID aleatório),
      // para a numeração seguir a ordem em que as fotos foram enviadas.
      result.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

      setThumbnails(result);

      setIsLoadingPhotos(false);
    } else {
      // Se não há fotos, limpar thumbnails
      setThumbnails([]);
      setIsLoadingPhotos(false);
    }
  }, [order]);

  const toggleSelected = useCallback((photo_id) => {
    setSelectedIds((state) =>
      state.includes(photo_id)
        ? state.filter((id) => id !== photo_id)
        : [...state, photo_id]
    );
  }, []);

  const toggleSelectAll = useCallback(() => {
    setSelectedIds((state) =>
      state.length === editedPhotos.length ? [] : editedPhotos.map((p) => p.id)
    );
  }, [editedPhotos]);

  const removeSelected = useCallback(async () => {
    setLoading(true);

    const response = await deletePhotosOfOrder(order.id, selectedIds);

    if (!response.error) {
      response.delivery_date = new Date(response.delivery_date);

      if (response.payment_first_date) {
        response.payment_first_date = new Date(
          response.payment_first_date.replace(/-/g, ',')
        );
      }

      setOrder(response);

      setSelectedIds([]);
    }

    setLoading(false);
  }, [selectedIds, order]);

  useEffect(() => {
    // Se há fotos para carregar, mostrar loader imediatamente
    if (editedPhotos.length > 0) {
      setIsLoadingPhotos(true);
    }
    prepareThumbnails();
  }, [order]);

  // Índice global por id (mantém a navegação do modal e a numeração mesmo com as
  // fotos divididas em seções por classificação).
  const indexById = new Map(thumbnails.map((t, i) => [t.id, i]));

  const EDITED_SECTIONS = [
    { key: 'Fotolivro', label: 'Fotolivro' },
    { key: 'Foto avulsa', label: 'Foto avulsa' },
    { key: null, label: 'Não classificadas' },
  ];

  // FEAT-018 UX: dentro de cada seção, as fotos ficam agrupadas por TAMANHO
  // (na ordem do rol; sem tamanho por último) — mesma lógica das capturadas.
  const groupBySize = (items) =>
    [...PHOTO_SIZES, null]
      .map((size) => ({
        size,
        label: size || 'Sem tamanho',
        photos: items.filter((t) =>
          size ? t.photo_size === size : !t.photo_size
        ),
      }))
      .filter((group) => group.photos.length > 0);

  const sectionItems = (section) =>
    thumbnails.filter((t) =>
      section.key
        ? t.photo_classification === section.key
        : !t.photo_classification
    );

  // Numeração sequencial na ORDEM DE EXIBIÇÃO (seções e grupos de tamanho em
  // sequência), para não aparecer fora de ordem. A navegação do modal continua
  // usando o índice por id (indexById).
  const orderedForDisplay = EDITED_SECTIONS.flatMap((section) =>
    groupBySize(sectionItems(section)).flatMap((group) => group.photos)
  );
  const displayNumberById = new Map(
    orderedForDisplay.map((t, i) => [t.id, i + 1])
  );

  const renderCard = (thumbnail) => {
    const index = indexById.get(thumbnail.id);

    return (
      <Photo
        key={thumbnail.id}
        className="col-6 col-md-2"
        is_cover_photo={thumbnail.is_cover_photo}
        reproved={thumbnail.photo_status === 'Reprovada'}
        onClick={() =>
          setFirstPhotoToView({
            id: thumbnail.id,
            photo_status: thumbnail.photo_status,
            note: thumbnail.note,
            is_cover_photo: thumbnail.is_cover_photo,
            index,
          })
        }
      >
        <div className="card mt-2">
          <S3Image
            src={thumbnail.blob}
            alt="foto editada"
            data-bs-toggle="modal"
            data-bs-target="#modal-view-image"
            data-tip={
              thumbnail.photo_status === 'Reprovada'
                ? `REPROVADA: ${thumbnail.note || 'sem descrição'}`
                : undefined
            }
            refreshUrl={() => getSW3ThumbnailUrl(order.id, thumbnail.id)}
          />
          <div className="bg-check">
            {(canDownload || canRemove() || canClassify()) && (
              <input
                type="checkbox"
                className="form-check-input me-2"
                checked={selectedIds.includes(thumbnail.id)}
                onChange={() => toggleSelected(thumbnail.id)}
                onClick={(e) => e.stopPropagation()}
                data-tip="Selecionar"
              />
            )}

            {canDownload && (
              <FaFileDownload
                size={22}
                color="#fff"
                className="me-2 photo-action"
                data-tip="Baixar foto original"
                onClick={(e) => {
                  e.stopPropagation();
                  downloadOne(thumbnail.id);
                }}
              />
            )}

            <div className="status">
              {thumbnail.photo_status === 'Aguarda inspeção' && (
                <FcInfo size={30} data-tip="Aguarda inspeção" />
              )}

              {thumbnail.photo_status === 'Aprovada' && (
                <FcOk size={30} data-tip="Foto aprovada" />
              )}

              {thumbnail.photo_status === 'Reprovada' && (
                <FcCancel
                  className="reproved"
                  size={30}
                  color="red"
                  data-tip={thumbnail.note}
                />
              )}
            </div>

            {thumbnail.photo_size && (
              <span
                className="badge fw-bold size-badge"
                style={{ fontSize: '12px' }}
              >
                {thumbnail.photo_size}
              </span>
            )}

            <div className="index">
              <span>{displayNumberById.get(thumbnail.id)}</span>
            </div>
          </div>
        </div>
        <ReactTooltip />
      </Photo>
    );
  };

  return (
    <>
      {(editedPhotos.length > 0 || canUpload()) &&
        hasPermissions([
          'edit_photos',
          'edit_order',
          'edit_inspection',
          'seller_inspection',
          'print_photos',
          'plotter_photos',
        ]) && (
          <Container className="mt-5">
            <div className="text-center bg-dark">
              <h5 className="mt-2">FOTOS EDITADAS </h5>
            </div>

            {!validPhotos && (
              <div className="row justify-content-center">
                <div className="col-sm-6 col-md-6 mt-2">
                  <div className="alert alert-danger text-center" role="alert">
                    <b>ATENÇÃO!</b> Pedido sem uma foto principal.
                  </div>
                </div>
              </div>
            )}
            <div className="container mt-2">
              <div className="row">
                <div className="col-12 col-md-8">
                  {/* FEAT-018 UX: um botão de upload por TAMANHO × TIPO derivado
                      dos produtos (mesma lógica das capturadas) — a editada já
                      nasce com tamanho e classificação carimbados. Sem tamanhos
                      parametrizados, botão genérico como antes. */}
                  {canUpload() && uploadButtons.length > 0 && (
                    <div className="d-flex flex-wrap gap-2">
                      {uploadButtons.map((button) => (
                        <InputFile
                          key={`${button.classification}-${button.size}`}
                          type="button"
                          className={
                            button.classification === 'Fotolivro'
                              ? 'btn btn-primary'
                              : 'btn btn-secondary'
                          }
                        >
                          {button.classification === 'Fotolivro'
                            ? `add álbum ${button.size}`
                            : `add fotos ${button.size}`}
                          <input
                            type="file"
                            multiple="multiple"
                            onChange={(e) =>
                              onChangePhoto(
                                e,
                                button.size,
                                button.classification
                              )
                            }
                            accept="image/*"
                          />
                        </InputFile>
                      ))}
                    </div>
                  )}

                  {canUpload() && uploadButtons.length === 0 && (
                    <InputFile
                      type="button"
                      className="btn btn-secondary btn-block"
                    >
                      add fotos editadas
                      <input
                        type="file"
                        multiple="multiple"
                        onChange={onChangePhoto}
                        accept="image/*"
                      />
                    </InputFile>
                  )}
                </div>

                <div className="col-12 col-md-4 text-end total-fotos">
                  {canDownload && editedPhotos.length > 0 && (
                    <button
                      type="button"
                      className="btn btn-sm btn-secondary me-2"
                      onClick={() => downloadAll()}
                    >
                      {downloadAllLabel()} <FaFileDownload className="mb-1" />
                    </button>
                  )}
                  <p className="d-inline">
                    Total de fotos{' '}
                    <span className="badge bg-secondary">
                      {editedPhotos.length}
                    </span>
                  </p>
                </div>
              </div>

              {/* Barra de seleção com BOTÕES evidentes (20/07): selecionar/
                  desmarcar sempre visíveis; ações ficam desabilitadas até
                  haver seleção (em vez de aparecerem do nada). */}
              {(canDownload || canRemove() || canClassify()) &&
                editedPhotos.length > 0 && (
                  <div className="row align-items-center mt-2">
                    <div className="col-12 col-md-5">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => toggleSelectAll()}
                      >
                        {selectedIds.length === editedPhotos.length
                          ? 'desmarcar todas'
                          : 'selecionar todas'}
                      </button>
                      {selectedIds.length > 0 && (
                        <span className="ms-2" style={{ fontSize: '13px' }}>
                          {selectedIds.length} selecionada(s)
                        </span>
                      )}
                    </div>

                    <div className="col-12 col-md-7 text-md-end mt-2 mt-md-0">
                      {canDownload && (
                        <button
                          type="button"
                          className="btn btn-sm btn-secondary me-2"
                          disabled={selectedIds.length === 0}
                          onClick={() => downloadSelected()}
                        >
                          baixar selecionadas ({selectedIds.length})
                        </button>
                      )}

                      {canRemove() && (
                        <button
                          type="button"
                          className="btn btn-sm btn-danger"
                          disabled={selectedIds.length === 0}
                          data-bs-toggle={
                            selectedIds.length > 0 ? 'modal' : undefined
                          }
                          data-bs-target={
                            selectedIds.length > 0
                              ? '#modal-remove-photo'
                              : undefined
                          }
                        >
                          remover selecionadas ({selectedIds.length})
                        </button>
                      )}
                    </div>
                  </div>
                )}

              {canClassify() && selectedIds.length > 0 && (
                <div className="row align-items-center mt-2">
                  <div className="col-12 d-flex flex-wrap gap-2 align-items-center">
                    <select
                      className="form-select form-select-sm"
                      style={{ width: 'auto' }}
                      value={assignSize}
                      onChange={(e) => setAssignSize(e.target.value)}
                    >
                      <option value="">Tamanho...</option>
                      {availableSizes.map((size) => (
                        <option key={size} value={size}>
                          {size}
                        </option>
                      ))}
                    </select>

                    <select
                      className="form-select form-select-sm"
                      style={{ width: 'auto' }}
                      value={assignClassification}
                      onChange={(e) => setAssignClassification(e.target.value)}
                    >
                      <option value="">Classificação...</option>
                      {PHOTO_CLASSIFICATIONS.map((classification) => (
                        <option key={classification} value={classification}>
                          {classification}
                        </option>
                      ))}
                    </select>

                    <button
                      type="button"
                      className="btn btn-sm btn-primary text-nowrap"
                      disabled={!assignSize && !assignClassification}
                      onClick={() => applyAttributes()}
                    >
                      aplicar às selecionadas ({selectedIds.length})
                    </button>
                  </div>
                </div>
              )}

              <ViewImageModal
                order={{ ...order, photos: editedPhotos }}
                setOrder={setOrder}
                getOrder={getOrder}
                first_photo_to_view={firstPhotoToView}
                inspection_status="Inspeção de edição"
              />

              <div className="row">
                <Legend>
                  <div>
                    <FcInfo size={15} />
                    <span>Aguarda inspeção</span>
                  </div>

                  <div>
                    <FcOk size={15} />
                    <span>Aprovado</span>
                  </div>

                  <div>
                    <FcCancel size={15} />
                    <span>Reprovado</span>
                  </div>

                  <div>
                    <FiSquare color="#d11515" size={15} />
                    <span>Foto principal</span>
                  </div>
                </Legend>

                {isLoadingPhotos && (
                  <PhotosLoaderContainer>
                    <PhotosSpinner />
                    <LoadingText>Carregando fotos do S3...</LoadingText>
                  </PhotosLoaderContainer>
                )}

                {!isLoadingPhotos &&
                  thumbnails.length === 0 &&
                  editedPhotos.length === 0 && (
                    <NoPhotosContainer>
                      <NoPhotosText>
                        Nenhuma foto disponível para este pedido.
                      </NoPhotosText>
                    </NoPhotosContainer>
                  )}

                {!isLoadingPhotos &&
                  EDITED_SECTIONS.map((section) => {
                    const items = sectionItems(section);

                    if (items.length === 0) return null;

                    return (
                      <div
                        key={section.label}
                        className="col-12"
                        style={{ clear: 'both' }}
                      >
                        <div
                          className="section-title"
                          style={{
                            position: 'relative',
                            zIndex: 5,
                            width: '100%',
                            display: 'block',
                            backgroundColor: '#f2f2f2',
                            borderLeft: '4px solid #ffba00',
                            padding: '8px 10px',
                            margin: '40px 0 14px',
                            fontWeight: 'bold',
                            fontSize: '15px',
                            color: '#464646',
                            textTransform: 'none',
                          }}
                        >
                          {section.label}{' '}
                          <span style={{ fontWeight: 'normal', color: '#888' }}>
                            ({items.length})
                          </span>
                          {/* Alerta visual: X de N fotos (não bloqueia). */}
                          {section.key === 'Fotolivro' &&
                            expectedAlbumPhotos > 0 && (
                              <span
                                style={{
                                  marginLeft: '10px',
                                  padding: '2px 10px',
                                  borderRadius: '10px',
                                  fontSize: '12px',
                                  fontWeight: 600,
                                  backgroundColor:
                                    items.length >= expectedAlbumPhotos
                                      ? '#c8e6c9'
                                      : '#fff3cd',
                                  color:
                                    items.length >= expectedAlbumPhotos
                                      ? '#1b5e20'
                                      : '#856404',
                                }}
                              >
                                {items.length} de {expectedAlbumPhotos} fotos do
                                álbum
                              </span>
                            )}
                          {section.key === 'Foto avulsa' &&
                            expectedAvulsaPhotos > 0 && (
                              <span
                                style={{
                                  marginLeft: '10px',
                                  padding: '2px 10px',
                                  borderRadius: '10px',
                                  fontSize: '12px',
                                  fontWeight: 600,
                                  backgroundColor:
                                    items.length >= expectedAvulsaPhotos
                                      ? '#c8e6c9'
                                      : '#fff3cd',
                                  color:
                                    items.length >= expectedAvulsaPhotos
                                      ? '#1b5e20'
                                      : '#856404',
                                }}
                              >
                                {items.length} de {expectedAvulsaPhotos} fotos
                                avulsas
                              </span>
                            )}
                        </div>

                        {/* FEAT-018 UX: subgrupos por TAMANHO dentro da seção,
                            com contador (enviadas/esperadas) — verde quando
                            bate, âmbar quando não (igual capturadas). */}
                        {groupBySize(items).map((group) => {
                          const expected = expectedForGroup(
                            section.key,
                            group.size
                          );

                          return (
                            <div key={group.label}>
                              <hr
                                style={{
                                  margin: '22px 0 10px',
                                  borderTop: '1px solid #d0d0d0',
                                  opacity: 1,
                                }}
                              />

                              <div
                                style={{
                                  margin: '0 0 6px',
                                  paddingLeft: '10px',
                                  textTransform: 'none',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px',
                                }}
                              >
                                <span
                                  style={{
                                    backgroundColor: '#464646',
                                    color: '#fff',
                                    fontWeight: 700,
                                    fontSize: '13px',
                                    padding: '2px 14px',
                                    borderRadius: '12px',
                                  }}
                                >
                                  {group.label}
                                </span>
                                {expected ? (
                                  <span
                                    style={{
                                      fontWeight: 700,
                                      fontSize: '13px',
                                      color:
                                        group.photos.length >= expected
                                          ? '#1b5e20'
                                          : '#856404',
                                    }}
                                  >
                                    {group.photos.length}/{expected}
                                  </span>
                                ) : (
                                  <span
                                    style={{ fontSize: '13px', color: '#888' }}
                                  >
                                    ({group.photos.length})
                                  </span>
                                )}
                              </div>

                              <div className="row">
                                {group.photos.map((t) => renderCard(t))}
                              </div>
                            </div>
                          );
                        })}

                        <hr
                          style={{
                            margin: '22px 0 0',
                            borderTop: '1px solid #d0d0d0',
                            opacity: 1,
                          }}
                        />
                      </div>
                    );
                  })}
              </div>
            </div>
          </Container>
        )}

      <DeleteModal
        id_modal="modal-remove-photo"
        item_delete={{ description: 'fotos selecionadas' }}
        deleteItem={() => removeSelected()}
      />
    </>
  );
};

EditedPhotos.propTypes = {
  order: PropTypes.shape({
    id: PropTypes.string,
    order_status: PropTypes.string,
    photos: PropTypes.arrayOf(Object),
    products: PropTypes.arrayOf(Object),
    person: PropTypes.shape({ full_name: PropTypes.string }),
  }).isRequired,
  setOrder: PropTypes.func.isRequired,
  getOrder: PropTypes.func.isRequired,
};

export default EditedPhotos;

export const Container = styled.section`
  text-transform: uppercase;
  margin-bottom: 50px;

  h6 {
    font-size: 12px;
  }

  .badge {
    background-color: #464646 !important;
  }

  .bg-dark {
    height: 40px;
    background-color: #464646 !important;
    display: flex;
    flex-direction: row;
    justify-content: center;

    h5 {
      color: white;
    }
  }

  .table {
    font-size: 13px;
  }

  .table-hover {
    cursor: pointer;
  }

  .action {
    color: #d11515;

    :hover {
      cursor: pointer;
      color: #ec4646;
    }
  }

  @media screen and (max-width: 768px) {
    .table {
      width: 900px !important;
    }

    .total-fotos {
      font-size: 12px;
    }
  }
`;

export const OrderField = styled.div`
  svg {
    margin-left: 8px;
    margin-top: -1px;
    :hover {
      color: #ffba00;
    }
  }
`;

export const Photo = styled.div`
  img {
    width: 100%;
    height: 150px;
    object-fit: cover;

    /* Reprovada: borda vermelha TRACEJADA (destaque; a sólida é da capa). */
    border: ${(props) =>
      props.reproved
        ? '4px dashed #dc3545'
        : props.is_cover_photo
        ? '5px solid #d11515'
        : ''};
  }

  /* Tooltip (motivo de reprovação/observação): quebra linha em textos longos
     e respeita as quebras digitadas, em vez de uma linha única gigante. */
  .__react_component_tooltip {
    max-width: 320px;
    white-space: pre-wrap;
    word-break: break-word;
    text-transform: none;
    text-align: left;
    font-size: 12px;

    :hover {
      cursor: pointer;

      box-shadow: 0 0 10px #464646;
    }
  }

  .bg-check {
    margin-top: -30px;
    padding-left: 5px;
    background: rgba(0, 0, 0, 0.5);

    .form-check-label {
      color: #fff;
    }

    /* Barrinha do card (20/07): checkbox GRANDE com borda branca — o padrão
       do Bootstrap era miúdo e passava despercebido sobre a foto. */
    .form-check-input {
      width: 20px;
      height: 20px;
      cursor: pointer;
      border: 2px solid #fff;
      vertical-align: middle;
    }

    /* Ícones de ação da barrinha (baixar/observação): feedback de hover. */
    .photo-action {
      cursor: pointer;

      :hover {
        transform: scale(1.2);
      }
    }

    /* O Container tem ".badge { background: #464646 !important }". Esta regra
       mais específica vence e deixa o tag de tamanho legível (amarelo). */
    .size-badge {
      background-color: #ffba00 !important;
      color: #212529 !important;
    }

    .index {
      top: 0;
      background-color: #ffba00;
      color: #464646;
      opacity: 0.7;
      width: 30px;
      height: 30px;
      border-radius: 50%;
      display: flex;
      font-weight: bold;
      justify-content: center;
      align-items: center;
      position: absolute;
      z-index: 1;
      right: 0;
      margin: 2px;
    }

    .status {
      top: 0;
      display: flex;
      font-weight: bold;
      position: absolute;
      z-index: 1;
      left: 0;
      margin: 2px;
    }
  }
`;

export const Legend = styled.div`
  display: flex;
  flex-direction: row;
  font-size: 11px;
  text-transform: none;
  align-items: center;

  svg {
    margin-top: -2px;
  }

  span {
    margin: 0 10px 0 5px;
  }

  @media screen and (max-width: 768px) {
    justify-content: center;
  }
`;

// Animação para o spinner das fotos
const rotate360 = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

// Container do loader das fotos
export const PhotosLoaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 40px 20px;
  width: 100%;
  min-height: 200px;
`;

// Spinner das fotos
export const PhotosSpinner = styled.div`
  animation: ${rotate360} 1s linear infinite;
  transform: translateZ(0);
  border-top: 4px solid #464646;
  border-right: 4px solid #464646;
  border-bottom: 4px solid #464646;
  border-left: 4px solid #ffba00;
  background: transparent;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  margin-bottom: 15px;
`;

// Texto do loader das fotos
export const LoadingText = styled.p`
  color: #464646;
  font-size: 14px;
  font-weight: 500;
  text-transform: none;
  margin: 0;
`;

// Container para mensagem de sem fotos
export const NoPhotosContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px 20px;
  width: 100%;
  min-height: 150px;
`;

// Texto para quando não há fotos
export const NoPhotosText = styled.p`
  color: #666;
  font-size: 16px;
  font-style: italic;
  text-transform: none;
  margin: 0;
`;
